import React, { useEffect, useMemo, useState } from "react";
import {
  Routes,
  Route,
  NavLink,
  Link,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { Helmet } from "react-helmet-async";

import projectsData from "./content/projects.json";
import "./App.css";

const BRAND = {
  name: "Aurelius Design Group",
  tagline: "Designing Prestige Across Sea, State, and Skyline.",
  founded: 2012,
  regions: ["U.S.", "South America", "Puerto Rico"],
  founder: "Sam Van",
  email: "proposals@aureliusdesign.group"
};

const SECTORS = [
  { key: "Yacht", slug: "yachts", label: "Yachts" },
  { key: "Government", slug: "government", label: "Government" },
  { key: "Hotel", slug: "hotels", label: "Hotels" },
  { key: "Casino", slug: "casinos", label: "Casinos" }
];

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

function useScrollToTopOnRouteChange() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);
}

function formatSector(sector) {
  const found = SECTORS.find((s) => s.key === sector);
  return found ? found.label : sector;
}

function formatRegion(region) {
  return region;
}

function getUniqueYears(items) {
  const years = Array.from(new Set(items.map((p) => p.year))).sort((a, b) => b - a);
  return years;
}

function App() {
  useScrollToTopOnRouteChange();

  return (
    <div
      data-testid="app-root"
      className="min-h-screen bg-stoneink-950 bg-radial-fade text-white"
    >
      <SiteHeader />
      <main className="fade-in">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:slug" element={<ProjectDetailPage />} />
          <Route path="/sectors" element={<SectorsPage />} />
          <Route path="/sectors/:sectorSlug" element={<SectorDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  const linkBase =
    "text-sm tracking-[0.18em] uppercase text-white/80 hover:text-white transition";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-white/10 bg-stoneink-950/70 backdrop-blur"
    >
      <div className="container-x flex items-center justify-between py-4">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="group inline-flex items-center gap-3"
        >
          <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-aureliusGold-400/40 bg-black/20 shadow-luxe">
            <span className="h-1.5 w-1.5 rounded-full bg-aureliusGold-400" />
          </span>
          <div className="leading-tight">
            <div className="font-display text-base text-white">{BRAND.name}</div>
            <div className="text-xs tracking-luxe uppercase text-white/60">
              Interior Architecture
            </div>
          </div>
        </Link>

        <nav data-testid="site-nav" className="hidden items-center gap-7 md:flex">
          <NavItem to="/portfolio" testId="nav-portfolio-link" label="Portfolio" />
          <NavItem to="/sectors" testId="nav-sectors-link" label="Sectors" />
          <NavItem to="/about" testId="nav-about-link" label="About" />
          <NavItem to="/contact" testId="nav-contact-link" label="Contact" />
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-request-proposal-button"
            href={`mailto:${BRAND.email}?subject=${encodeURIComponent(
              "Request Proposal — Aurelius Design Group"
            )}`}
            className="hidden rounded-full border border-aureliusGold-400/40 bg-aureliusGold-400/10 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/90 hover:bg-aureliusGold-400/15 md:inline-block"
          >
            Request Proposal
          </a>
          <MobileMenu />
        </div>
      </div>
      <div className="container-x pb-3 md:hidden">
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <NavLink
            to="/portfolio"
            data-testid="nav-portfolio-link-mobile"
            className={({ isActive }) =>
              cx(linkBase, isActive && "text-white")
            }
          >
            Portfolio
          </NavLink>
          <NavLink
            to="/sectors"
            data-testid="nav-sectors-link-mobile"
            className={({ isActive }) => cx(linkBase, isActive && "text-white")}
          >
            Sectors
          </NavLink>
          <NavLink
            to="/about"
            data-testid="nav-about-link-mobile"
            className={({ isActive }) => cx(linkBase, isActive && "text-white")}
          >
            About
          </NavLink>
          <NavLink
            to="/contact"
            data-testid="nav-contact-link-mobile"
            className={({ isActive }) => cx(linkBase, isActive && "text-white")}
          >
            Contact
          </NavLink>
        </div>
      </div>
    </header>
  );
}

function NavItem({ to, label, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cx(
          "text-xs tracking-[0.22em] uppercase transition",
          isActive ? "text-white" : "text-white/70 hover:text-white"
        )
      }
    >
      {label}
    </NavLink>
  );
}

function MobileMenu() {
  // Classic top nav chosen; on mobile we use inline links + mailto button.
  return (
    <a
      data-testid="nav-request-proposal-button-mobile"
      href={`mailto:${BRAND.email}?subject=${encodeURIComponent(
        "Request Proposal — Aurelius Design Group"
      )}`}
      className="md:hidden rounded-full border border-aureliusGold-400/40 bg-aureliusGold-400/10 px-3 py-2 text-[11px] tracking-[0.18em] uppercase text-white/90"
    >
      Proposal
    </a>
  );
}

function SiteFooter() {
  return (
    <footer
      data-testid="site-footer"
      className="mt-24 border-t border-white/10"
    >
      <div className="container-x grid gap-10 py-14 md:grid-cols-3">
        <div>
          <div className="font-display text-lg text-white">{BRAND.name}</div>
          <div className="mt-3 text-sm text-white/70">
            Designing prestige across sea, state, and skyline.
          </div>
          <div className="mt-6 text-xs tracking-[0.22em] uppercase text-white/50">
            © {BRAND.founded}–{new Date().getFullYear()} {BRAND.name}. All Rights
            Reserved.
          </div>
        </div>

        <div>
          <div className="text-xs tracking-[0.22em] uppercase text-white/60">
            Contact
          </div>
          <div className="mt-4 space-y-2 text-sm text-white/75">
            <a
              data-testid="footer-email-link"
              className="block hover:text-white"
              href={`mailto:${BRAND.email}`}
            >
              {BRAND.email}
            </a>
            <div data-testid="footer-regions" className="text-white/60">
              {BRAND.regions.join(" · ")}
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs tracking-[0.22em] uppercase text-white/60">
            Links
          </div>
          <div className="mt-4 grid gap-2 text-sm text-white/75">
            <Link data-testid="footer-portfolio-link" to="/portfolio" className="hover:text-white">
              Portfolio
            </Link>
            <Link data-testid="footer-contact-link" to="/contact" className="hover:text-white">
              Request Proposal
            </Link>
            <Link data-testid="footer-privacy-link" to="/about" className="hover:text-white">
              Privacy / Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function HomePage() {
  const featured = useMemo(() => projectsData.slice(0, 6), []);

  return (
    <>
      <Helmet>
        <title>{BRAND.name}</title>
        <meta name="description" content={BRAND.tagline} />
      </Helmet>

      <section data-testid="home-hero" className="pt-14 md:pt-20">
        <div className="container-x">
          <div className="grid gap-10 md:grid-cols-12 md:items-end">
            <div className="md:col-span-8">
              <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                Founded {BRAND.founded}
              </div>
              <h1 className="mt-5 font-display text-4xl leading-tight text-white md:text-6xl">
                {BRAND.tagline}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70">
                High-complexity interiors across yachts and superyachts, government facilities,
                luxury hospitality, and casino destinations—delivered with discretion,
                compliance readiness, and craftsmanship.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/portfolio"
                  data-testid="home-view-portfolio-button"
                  className="rounded-full bg-white px-6 py-3 text-xs tracking-[0.22em] uppercase text-stoneink-950 hover:bg-white/90"
                >
                  View Portfolio
                </Link>
                <a
                  href={`mailto:${BRAND.email}?subject=${encodeURIComponent(
                    "Request Proposal — Aurelius Design Group"
                  )}`}
                  data-testid="home-request-proposal-button"
                  className="rounded-full border border-aureliusGold-400/50 bg-aureliusGold-400/10 px-6 py-3 text-xs tracking-[0.22em] uppercase text-white hover:bg-aureliusGold-400/15"
                >
                  Request Proposal
                </a>
              </div>
            </div>

            <div className="md:col-span-4">
              <div className="rounded-2xl border border-white/10 bg-black/25 p-6 shadow-luxe">
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">
                  Regions
                </div>
                <div data-testid="home-regions" className="mt-3 text-sm text-white/80">
                  {BRAND.regions.join(" · ")}
                </div>
                <div className="mt-8 text-xs tracking-[0.22em] uppercase text-white/60">
                  Leadership
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <img
                    data-testid="home-founder-headshot"
                    src="/headshots/sam-van.jpg"
                    alt="Sam Van"
                    className="h-16 w-16 rounded-full border border-aureliusGold-400/40 object-cover"
                    loading="lazy"
                  />
                  <div>
                    <div data-testid="home-founder-name" className="font-display text-lg">
                      {BRAND.founder}
                    </div>
                    <div className="text-sm text-white/65">Founder & CEO</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-4">
            {SECTORS.map((s) => (
              <Link
                key={s.key}
                to={`/sectors/${s.slug}`}
                data-testid={`home-sector-card-${s.slug}`}
                className="group rounded-2xl border border-white/10 bg-black/20 p-6 transition hover:border-aureliusGold-400/40 hover:bg-black/30"
              >
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">
                  Sector
                </div>
                <div className="mt-3 font-display text-xl text-white">
                  {s.label}
                </div>
                <div className="mt-4 text-sm text-white/60">
                  Selected work and capabilities.
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-featured-projects" className="pt-16 md:pt-20">
        <div className="container-x">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                Featured Projects
              </div>
              <h2 className="mt-4 font-display text-3xl text-white md:text-4xl">
                A disciplined portfolio—visual first.
              </h2>
            </div>
            <Link
              to="/portfolio"
              data-testid="home-featured-view-all-link"
              className="hidden text-xs tracking-[0.22em] uppercase text-white/70 hover:text-white md:inline"
            >
              View all →
            </Link>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {featured.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                testIdPrefix="home-featured"
              />
            ))}
          </div>

          <div className="mt-10 md:hidden">
            <Link
              to="/portfolio"
              data-testid="home-featured-view-all-link-mobile"
              className="inline-flex rounded-full border border-white/15 px-5 py-3 text-xs tracking-[0.22em] uppercase text-white/80"
            >
              View all projects
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="home-institutional" className="pt-20">
        <div className="container-x">
          <div className="rounded-3xl border border-white/10 bg-black/25 p-10 shadow-luxe md:p-14">
            <div className="grid gap-10 md:grid-cols-12">
              <div className="md:col-span-7">
                <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                  Institutional + Luxury
                </div>
                <h3 className="mt-5 font-display text-3xl text-white">
                  Discretion. Compliance-readiness. Craftsmanship.
                </h3>
                <p className="mt-6 text-base leading-relaxed text-white/70">
                  We serve stakeholders who expect a controlled process, accurate documentation,
                  and a refined built result. From sensitive government programs to high-profile
                  hospitality and marine environments—our work protects reputation and schedule.
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="space-y-4">
                  <ValueItem label="Discretion" text="Confidential workflows and minimal exposure." />
                  <ValueItem label="Compliance" text="Materials, security, and accessibility readiness." />
                  <ValueItem label="Delivery" text="On-time coordination across vendors and trades." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ValueItem({ label, text }) {
  return (
    <div
      data-testid={`home-value-${label.toLowerCase()}`}
      className="rounded-2xl border border-white/10 bg-black/20 p-5"
    >
      <div className="text-xs tracking-[0.22em] uppercase text-white/60">{label}</div>
      <div className="mt-2 text-sm leading-relaxed text-white/75">{text}</div>
    </div>
  );
}

function PortfolioPage() {
  const allYears = useMemo(() => getUniqueYears(projectsData), []);

  const [sector, setSector] = useState("All");
  const [region, setRegion] = useState("All");
  const [year, setYear] = useState("All");

  const filtered = useMemo(() => {
    return projectsData.filter((p) => {
      const sectorOk = sector === "All" || p.sector === sector;
      const regionOk = region === "All" || p.region === region;
      const yearOk = year === "All" || String(p.year) === String(year);
      return sectorOk && regionOk && yearOk;
    });
  }, [sector, region, year]);

  const regions = useMemo(() => {
    const list = Array.from(new Set(projectsData.map((p) => p.region)));
    return ["All", ...list];
  }, []);

  return (
    <>
      <Helmet>
        <title>Portfolio — {BRAND.name}</title>
      </Helmet>

      <section data-testid="portfolio-page" className="pt-12">
        <div className="container-x">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                Portfolio
              </div>
              <h1 className="mt-4 font-display text-4xl text-white md:text-5xl">
                Projects
              </h1>
              <p className="mt-5 max-w-2xl text-white/70">
                Filter by sector and region. Detail pages are generated from local JSON.
              </p>
            </div>

            <div className="grid w-full gap-3 md:w-auto md:grid-cols-3">
              <FilterSelect
                label="Sector"
                testId="portfolio-sector-filter"
                value={sector}
                onChange={setSector}
                options={["All", ...SECTORS.map((s) => s.key)]}
              />
              <FilterSelect
                label="Region"
                testId="portfolio-region-filter"
                value={region}
                onChange={setRegion}
                options={regions}
              />
              <FilterSelect
                label="Year"
                testId="portfolio-year-filter"
                value={year}
                onChange={setYear}
                options={["All", ...allYears.map(String)]}
              />
            </div>
          </div>

          <div className="mt-10">
            <div
              data-testid="portfolio-results-count"
              className="text-xs tracking-[0.22em] uppercase text-white/60"
            >
              Showing {filtered.length} projects
            </div>
          </div>

          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {filtered.map((p) => (
              <ProjectCard key={p.id} project={p} testIdPrefix="portfolio" />
            ))}
            {filtered.length === 0 ? (
              <div
                data-testid="portfolio-no-results"
                className="col-span-full rounded-2xl border border-white/10 bg-black/20 p-8 text-white/70"
              >
                No projects match the current filters.
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label
      data-testid={`${testId}-wrapper`}
      className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3"
    >
      <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">
        {label}
      </div>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent text-sm text-white/85 outline-none"
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-stoneink-900">
            {opt === "All" ? "All" : opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProjectCard({ project, testIdPrefix }) {
  return (
    <Link
      to={`/portfolio/${project.slug}`}
      data-testid={`${testIdPrefix}-project-card-${project.slug}`}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-black/20 transition hover:border-aureliusGold-400/40"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          data-testid={`${testIdPrefix}-project-card-image-${project.slug}`}
          src={project.heroImage}
          alt={project.name}
          className="h-full w-full object-cover opacity-95 transition duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
        {project.confidential ? (
          <div
            data-testid={`${testIdPrefix}-project-confidential-${project.slug}`}
            className="absolute left-4 top-4 rounded-full border border-aureliusGold-400/40 bg-black/40 px-3 py-1 text-[11px] tracking-[0.22em] uppercase text-white"
          >
            Confidential
          </div>
        ) : null}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-xl text-white">{project.name}</div>
            <div className="mt-2 text-sm text-white/65">{project.location}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] tracking-[0.22em] uppercase text-white/55">
              {formatSector(project.sector)}
            </div>
            <div className="mt-2 text-sm text-white/65">{project.year}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ProjectDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const project = useMemo(
    () => projectsData.find((p) => p.slug === slug),
    [slug]
  );

  if (!project) {
    return (
      <section data-testid="project-not-found" className="pt-14">
        <div className="container-x">
          <div className="rounded-3xl border border-white/10 bg-black/20 p-10">
            <div className="font-display text-3xl">Project not found</div>
            <p className="mt-4 text-white/70">
              The requested project page does not exist.
            </p>
            <button
              data-testid="project-not-found-back-button"
              onClick={() => navigate("/portfolio")}
              className="mt-8 rounded-full border border-white/15 px-5 py-3 text-xs tracking-[0.22em] uppercase text-white/85"
            >
              Back to Portfolio
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <Helmet>
        <title>{project.name} — {BRAND.name}</title>
        <meta name="description" content={project.scope} />
      </Helmet>

      <article data-testid="project-detail-page" className="pt-10">
        <div className="container-x">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/portfolio"
              data-testid="project-detail-back-link"
              className="text-xs tracking-[0.22em] uppercase text-white/70 hover:text-white"
            >
              ← Back to portfolio
            </Link>

            {project.confidential ? (
              <div
                data-testid="project-detail-confidential-badge"
                className="rounded-full border border-aureliusGold-400/40 bg-black/30 px-3 py-1 text-[11px] tracking-[0.22em] uppercase text-white"
              >
                Confidential
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-8">
          <div className="container-x">
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/20">
              <div className="relative aspect-[21/9]">
                <img
                  data-testid="project-detail-hero-image"
                  src={project.heroImage}
                  alt={project.name}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="grid gap-8 p-8 md:grid-cols-12 md:p-10">
                <div className="md:col-span-7">
                  <h1
                    data-testid="project-detail-title"
                    className="font-display text-3xl text-white md:text-4xl"
                  >
                    {project.name}
                  </h1>
                  <p
                    data-testid="project-detail-scope"
                    className="mt-5 text-base leading-relaxed text-white/70"
                  >
                    {project.confidential
                      ? "Project details are limited by client confidentiality."
                      : project.scope}
                  </p>
                </div>
                <div className="md:col-span-5">
                  <dl className="grid gap-4 rounded-2xl border border-white/10 bg-black/25 p-6">
                    <InfoRow label="Sector" value={formatSector(project.sector)} testId="project-detail-sector" />
                    <InfoRow label="Region" value={formatRegion(project.region)} testId="project-detail-region" />
                    <InfoRow label="Location" value={project.location} testId="project-detail-location" />
                    <InfoRow label="Year" value={String(project.year)} testId="project-detail-year" />
                  </dl>
                  <a
                    data-testid="project-detail-request-proposal-button"
                    href={`mailto:${BRAND.email}?subject=${encodeURIComponent(
                      `Proposal Request — ${project.name}`
                    )}`}
                    className="mt-5 inline-flex w-full items-center justify-center rounded-full border border-aureliusGold-400/50 bg-aureliusGold-400/10 px-6 py-3 text-xs tracking-[0.22em] uppercase text-white hover:bg-aureliusGold-400/15"
                  >
                    Request proposal
                  </a>
                </div>
              </div>
            </div>

            <section data-testid="project-detail-gallery" className="mt-10">
              <div className="flex items-end justify-between gap-6">
                <div>
                  <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                    Gallery
                  </div>
                  <h2 className="mt-4 font-display text-2xl text-white">
                    {project.confidential ? "Selected visuals" : "Project visuals"}
                  </h2>
                </div>
              </div>

              <div
                data-testid="project-detail-gallery-count"
                className="mt-4 text-xs tracking-[0.22em] uppercase text-white/60"
              >
                Showing {Math.min(project.gallery.length, 20)} of {project.gallery.length} images
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-3">
                {project.gallery.slice(0, 20).map((src, idx) => (
                  <div
                    key={`${src}-${idx}`}
                    data-testid={`project-detail-gallery-item-${idx}`}
                    className="overflow-hidden rounded-2xl border border-white/10 bg-black/20"
                  >
                    <img
                      src={src}
                      alt={`${project.name} gallery ${idx + 1}`}
                      className="aspect-[16/11] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>
    </>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <dt className="text-[11px] tracking-[0.22em] uppercase text-white/55">{label}</dt>
      <dd data-testid={testId} className="text-sm text-white/80">
        {value}
      </dd>
    </div>
  );
}

function SectorsPage() {
  return (
    <>
      <Helmet>
        <title>Sectors — {BRAND.name}</title>
      </Helmet>

      <section data-testid="sectors-page" className="pt-12">
        <div className="container-x">
          <div>
            <div className="text-xs tracking-[0.28em] uppercase text-white/60">
              Sectors
            </div>
            <h1 className="mt-4 font-display text-4xl text-white md:text-5xl">
              Where we deliver.
            </h1>
            <p className="mt-5 max-w-2xl text-white/70">
              Four disciplined verticals—each with its own constraints, stakeholders, and
              required execution.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {SECTORS.map((s) => (
              <Link
                key={s.key}
                to={`/sectors/${s.slug}`}
                data-testid={`sectors-card-${s.slug}`}
                className="group rounded-3xl border border-white/10 bg-black/20 p-8 transition hover:border-aureliusGold-400/40 hover:bg-black/30"
              >
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">
                  Sector
                </div>
                <div className="mt-4 font-display text-3xl text-white">
                  {s.label}
                </div>
                <div className="mt-4 text-white/70">
                  Capabilities + selected work.
                </div>
                <div className="mt-8 text-xs tracking-[0.22em] uppercase text-white/70">
                  View →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function SectorDetailPage() {
  const { sectorSlug } = useParams();
  const sector = useMemo(
    () => SECTORS.find((s) => s.slug === sectorSlug),
    [sectorSlug]
  );

  const projects = useMemo(() => {
    if (!sector) return [];
    return projectsData.filter((p) => p.sector === sector.key);
  }, [sector]);

  if (!sector) {
    return <NotFoundPage />;
  }

  const intro = {
    yachts:
      "Marine interiors demand weight discipline, vibration-aware detailing, and uncompromising finish execution.",
    government:
      "Government environments require secure planning, controlled documentation, and compliance-ready materials.",
    hotels:
      "Hospitality design is operational design—arrival sequences, durability, and refined guest experience at scale.",
    casinos:
      "Casino destinations balance performance lighting, acoustics, circulation, and brand-grade luxury."
  }[sectorSlug];

  const capabilities = {
    yachts: [
      "Owner suite + guest cabin redesign",
      "Lighting plans + fixture schedules",
      "Material standards and specifications",
      "Coordination with shipyards and vendors"
    ],
    government: [
      "Secure meeting environments",
      "Compliance-ready finishes + documentation",
      "Stakeholder reception + executive suites",
      "Phased delivery and confidentiality"
    ],
    hotels: [
      "Lobby + arrival sequences",
      "Guestroom prototypes and standards",
      "FF&E + millwork packages",
      "Value engineering without losing brand"
    ],
    casinos: [
      "VIP suites and lounges",
      "Wayfinding and floor refresh",
      "Lighting + acoustic control",
      "High-durability material selection"
    ]
  }[sectorSlug];

  return (
    <>
      <Helmet>
        <title>{sector.label} — {BRAND.name}</title>
      </Helmet>

      <section data-testid="sector-detail-page" className="pt-12">
        <div className="container-x">
          <Link
            to="/sectors"
            data-testid="sector-detail-back-link"
            className="text-xs tracking-[0.22em] uppercase text-white/70 hover:text-white"
          >
            ← All sectors
          </Link>

          <div className="mt-6 grid gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                Sector
              </div>
              <h1
                data-testid="sector-detail-title"
                className="mt-4 font-display text-4xl text-white md:text-5xl"
              >
                {sector.label}
              </h1>
              <p data-testid="sector-detail-intro" className="mt-5 text-white/70">
                {intro}
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-7">
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">
                  Capabilities
                </div>
                <ul className="mt-5 space-y-3">
                  {capabilities.map((c) => (
                    <li
                      key={c}
                      data-testid={`sector-detail-capability-${c
                        .toLowerCase()
                        .replace(/\W+/g, "-")}`}
                      className="text-sm text-white/75"
                    >
                      {c}
                    </li>
                  ))}
                </ul>
                <a
                  data-testid="sector-detail-request-proposal-button"
                  href={`mailto:${BRAND.email}?subject=${encodeURIComponent(
                    `Request Proposal — ${sector.label}`
                  )}`}
                  className="mt-7 inline-flex w-full items-center justify-center rounded-full border border-aureliusGold-400/50 bg-aureliusGold-400/10 px-6 py-3 text-xs tracking-[0.22em] uppercase text-white hover:bg-aureliusGold-400/15"
                >
                  Request proposal
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="text-xs tracking-[0.28em] uppercase text-white/60">
              Selected Projects
            </div>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {projects.map((p) => (
                <ProjectCard key={p.id} project={p} testIdPrefix={`sector-${sectorSlug}`} />
              ))}
              {projects.length === 0 ? (
                <div
                  data-testid="sector-detail-no-projects"
                  className="col-span-full rounded-2xl border border-white/10 bg-black/20 p-8 text-white/70"
                >
                  No projects in this sector yet.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About — {BRAND.name}</title>
      </Helmet>

      <section data-testid="about-page" className="pt-12">
        <div className="container-x">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                About
              </div>
              <h1 className="mt-4 font-display text-4xl text-white md:text-5xl">
                Founded {BRAND.founded}.
              </h1>
              <p className="mt-6 text-base leading-relaxed text-white/70">
                Aurelius Design Group is an interior architecture and design studio founded in 2012 by Sam Van.
                We deliver high-complexity environments across yachts and superyachts, government facilities,
                luxury hospitality, and casino destinations—serving clients throughout the United States,
                South America, and Puerto Rico.
              </p>
            </div>

            <div className="md:col-span-5">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-8 shadow-luxe">
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">Values</div>
                <div className="mt-5 space-y-4">
                  <ValueItem label="Discretion" text="Sensitive work handled with controlled visibility." />
                  <ValueItem label="Compliance-ready" text="Documentation and finish selection aligned to requirements." />
                  <ValueItem label="Craftsmanship" text="Details, coordination, and material integrity." />
                  <ValueItem label="On-time delivery" text="Clear scope and disciplined milestones." />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 overflow-hidden rounded-3xl border border-white/10 bg-black/20 shadow-luxe">
            <div className="grid gap-0 md:grid-cols-12">
              <div className="md:col-span-5">
                <div className="relative h-[420px] md:h-full md:min-h-[640px]">
                  <img
                    data-testid="about-founder-hero-image"
                    src="/headshots/sam-van-about.jpg"
                    alt="Sam Van"
                    className="absolute inset-0 h-full w-full object-cover object-[50%_0%]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stoneink-950/95 via-stoneink-950/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                    <div className="text-xs tracking-[0.22em] uppercase text-white/70">Leadership</div>
                    <div data-testid="about-founder" className="mt-3 font-display text-3xl text-white">
                      {BRAND.founder}
                    </div>
                    <div className="mt-1 text-sm text-white/70">Founder & CEO</div>
                  </div>
                </div>
              </div>

              <div className="md:col-span-7">
                <div className="p-10 md:p-12">
                  <div className="text-xs tracking-[0.22em] uppercase text-white/60">Summary</div>
                  <p data-testid="about-summary" className="mt-4 text-white/70">
                    Aurelius Design Group is built for environments where brand, scrutiny, and schedule are non‑negotiable.
                    We operate with a documentation-forward process—translating vision into buildable, procurement-ready packages
                    across marine, government, hospitality, and casino programs.
                  </p>

                  <div className="mt-10">
                    <div className="text-xs tracking-[0.22em] uppercase text-white/60">Partners / Vendors</div>
                    <p data-testid="about-partners" className="mt-4 text-white/70">
                      We collaborate with vetted shipyards, specialty fabricators, lighting and millwork partners,
                      and procurement teams to execute to standard—quietly and on schedule.
                    </p>
                  </div>

                  <div className="mt-10 grid gap-4 md:grid-cols-2">
                    <div
                      data-testid="about-capability-discretion"
                      className="rounded-2xl border border-white/10 bg-black/25 p-6"
                    >
                      <div className="text-xs tracking-[0.22em] uppercase text-white/60">Discretion</div>
                      <div className="mt-2 text-sm text-white/75">
                        Confidential workflows aligned to sensitive stakeholders.
                      </div>
                    </div>
                    <div
                      data-testid="about-capability-delivery"
                      className="rounded-2xl border border-white/10 bg-black/25 p-6"
                    >
                      <div className="text-xs tracking-[0.22em] uppercase text-white/60">Delivery</div>
                      <div className="mt-2 text-sm text-white/75">
                        Clean coordination across vendors, trades, and procurement.
                      </div>
                    </div>
                    <div
                      data-testid="about-capability-compliance"
                      className="rounded-2xl border border-white/10 bg-black/25 p-6"
                    >
                      <div className="text-xs tracking-[0.22em] uppercase text-white/60">Compliance-ready</div>
                      <div className="mt-2 text-sm text-white/75">
                        Materials and detailing aligned to program requirements.
                      </div>
                    </div>
                    <div
                      data-testid="about-capability-craft"
                      className="rounded-2xl border border-white/10 bg-black/25 p-6"
                    >
                      <div className="text-xs tracking-[0.22em] uppercase text-white/60">Craftsmanship</div>
                      <div className="mt-2 text-sm text-white/75">
                        Details and finish integrity carried through to install.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <Helmet>
        <title>Contact — {BRAND.name}</title>
      </Helmet>

      <section data-testid="contact-page" className="pt-12">
        <div className="container-x">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.28em] uppercase text-white/60">
                Contact
              </div>
              <h1 className="mt-4 font-display text-4xl text-white md:text-5xl">
                Request a proposal.
              </h1>
              <p className="mt-6 max-w-2xl text-white/70">
                For MVP we use a simple mailto workflow. Share sector, location, timeline, and any link to supporting documents.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  data-testid="contact-mailto-button"
                  href={`mailto:${BRAND.email}?subject=${encodeURIComponent(
                    "Request Proposal — Aurelius Design Group"
                  )}`}
                  className="rounded-full bg-white px-6 py-3 text-xs tracking-[0.22em] uppercase text-stoneink-950 hover:bg-white/90"
                >
                  Email proposals
                </a>
                <Link
                  to="/portfolio"
                  data-testid="contact-view-portfolio-button"
                  className="rounded-full border border-aureliusGold-400/50 bg-aureliusGold-400/10 px-6 py-3 text-xs tracking-[0.22em] uppercase text-white hover:bg-aureliusGold-400/15"
                >
                  View portfolio
                </Link>
              </div>

              <div className="mt-10 rounded-3xl border border-white/10 bg-black/20 p-8">
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">What to include</div>
                <ul className="mt-5 space-y-3 text-sm text-white/75">
                  <li data-testid="contact-guideline-1">Your name, organization, and role</li>
                  <li data-testid="contact-guideline-2">Project sector and location</li>
                  <li data-testid="contact-guideline-3">Desired timeline and stakeholder requirements</li>
                  <li data-testid="contact-guideline-4">Optional file link (Dropbox/Drive URL)</li>
                </ul>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-8 shadow-luxe">
                <div className="text-xs tracking-[0.22em] uppercase text-white/60">
                  Direct
                </div>
                <div className="mt-5">
                  <div className="text-sm text-white/60">Email</div>
                  <a
                    data-testid="contact-email-link"
                    href={`mailto:${BRAND.email}`}
                    className="mt-2 block font-display text-xl hover:text-white"
                  >
                    {BRAND.email}
                  </a>
                </div>
                <div className="mt-8">
                  <div className="text-sm text-white/60">Regions served</div>
                  <div data-testid="contact-regions" className="mt-3 text-white/75">
                    {BRAND.regions.join(" · ")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function NotFoundPage() {
  return (
    <section data-testid="not-found-page" className="pt-14">
      <div className="container-x">
        <div className="rounded-3xl border border-white/10 bg-black/20 p-10">
          <div className="font-display text-3xl">Page not found</div>
          <p className="mt-4 text-white/70">
            The page you’re looking for doesn’t exist.
          </p>
          <Link
            data-testid="not-found-home-link"
            to="/"
            className="mt-8 inline-flex rounded-full border border-white/15 px-5 py-3 text-xs tracking-[0.22em] uppercase text-white/85"
          >
            Return home
          </Link>
        </div>
      </div>
    </section>
  );
}

export default App;
